import { APP_ID } from '@i18n-ecom-seller/build-runtime/mainConfig';
import { reportBssdkEvent } from '@/monitoring';
import type { BssdkEventName } from '@/monitoring';
import {
  BssdkInitOptions,
  BssdkRuntimeConfig,
  buildBssdkInitOptions,
  resolveBssdkRuntimeConfig
} from './config';

const SCRIPT_MARKER = 'ticketEmailBssdk';
const DEFAULT_INIT_TIMEOUT_MS = 1200;
const CAPTCHA_DISCOVERY_TIMEOUT_MS = 300;
const CAPTCHA_RECOVERY_TIMEOUT_MS = 10000;
const CAPTCHA_RECOVERY_POLL_INTERVAL_MS = 100;

interface LuciferSdk {
  init: (options: BssdkInitOptions) => unknown;
  setUserMode?: (mode: number) => unknown;
}

interface CaptchaInitOptions {
  commonOptions: {
    aid: number;
    iid: string;
    did: string;
  };
  captchaOptions: {
    showMode: 'mask';
    successCb: () => void;
  };
}

interface OecCaptchaSdk {
  init: (
    options: CaptchaInitOptions,
    successCallback?: () => void,
    errorCallback?: (error: unknown) => void
  ) => unknown;
  render: (options: Record<string, unknown>) => unknown;
  getFp?: () => Promise<string>;
}

export type BssdkInitStatus = 'loading' | 'ready' | 'failed' | 'unavailable';
export type CaptchaInitStatus = 'pending' | 'ready' | 'failed' | 'unavailable';

export interface BssdkInitState {
  status: BssdkInitStatus;
  loaderUrl?: string;
  options?: BssdkInitOptions;
  captchaInitialized: boolean;
  captchaStatus: CaptchaInitStatus;
  error?: string;
}

declare global {
  interface Window {
    lucifer?: LuciferSdk;
    OECCaptcha?: OecCaptchaSdk;
    __TICKET_EMAIL_BSSDK_INIT_PROMISE__?: Promise<BssdkInitState>;
    __TICKET_EMAIL_BSSDK_STATE__?: BssdkInitState;
  }
}

interface InitializeBssdkOptions {
  timeoutMs?: number;
  captchaDiscoveryTimeoutMs?: number;
  captchaRecoveryTimeoutMs?: number;
  aid?: number;
}

const safeReport = (eventName: BssdkEventName, payload: Record<string, unknown>): void => {
  try {
    reportBssdkEvent(eventName, payload);
    // eslint-disable-next-line @i18n-ecom/no-meaningless/try-catch
  } catch (_error) {
    // Security initialization must never block the email form when monitoring is unavailable.
  }
};

const setState = (state: BssdkInitState): BssdkInitState => {
  window.__TICKET_EMAIL_BSSDK_STATE__ = state;
  return state;
};

const updateCaptchaState = (
  captchaInitialized: boolean,
  captchaStatus: CaptchaInitStatus
): void => {
  const currentState = window.__TICKET_EMAIL_BSSDK_STATE__;
  if (!currentState || currentState.status === 'failed' || currentState.status === 'unavailable') {
    return;
  }

  setState({
    ...currentState,
    captchaInitialized,
    captchaStatus
  });
};

const getRuntimeConfig = (): BssdkRuntimeConfig | undefined =>
  resolveBssdkRuntimeConfig({
    idcName: window.__SELLER_APP_ENVS__?.idc?.idc_name,
    hostname: window.location.hostname,
    search: window.location.search
  });

const findExistingScript = (loaderUrl: string): HTMLScriptElement | undefined =>
  Array.from(document.scripts).find(script => script.src === loaderUrl);

const loadBssdkScript = (loaderUrl: string): Promise<void> => {
  if (window.lucifer) {
    return Promise.resolve();
  }

  const existingScript = findExistingScript(loaderUrl);
  const script = existingScript ?? document.createElement('script');

  return new Promise((resolve, reject) => {
    if (window.lucifer) {
      resolve();
      return;
    }

    script.addEventListener('load', () => resolve(), { once: true });
    script.addEventListener('error', () => reject(new Error('BSSDK loader failed')), { once: true });

    if (!existingScript) {
      script.src = loaderUrl;
      script.async = true;
      script.dataset[SCRIPT_MARKER] = 'true';
      document.head.appendChild(script);
    }
  });
};

const waitForCaptcha = (
  timeoutMs: number,
  pollIntervalMs = 20
): Promise<OecCaptchaSdk | undefined> => {
  if (window.OECCaptcha) {
    return Promise.resolve(window.OECCaptcha);
  }

  return new Promise(resolve => {
    const startedAt = Date.now();
    const poll = (): void => {
      if (window.OECCaptcha) {
        resolve(window.OECCaptcha);
        return;
      }

      if (Date.now() - startedAt >= timeoutMs) {
        resolve(undefined);
        return;
      }

      window.setTimeout(poll, pollIntervalMs);
    };

    poll();
  });
};

interface CaptchaInitResult {
  initialized: boolean;
  status: CaptchaInitStatus;
}

const initializeCaptcha = async (
  aid: number,
  discoveryTimeoutMs: number,
  missingStatus: Extract<CaptchaInitStatus, 'pending' | 'unavailable'> = 'unavailable',
  pollIntervalMs?: number
): Promise<CaptchaInitResult> => {
  const captcha = await waitForCaptcha(discoveryTimeoutMs, pollIntervalMs);
  if (!captcha) {
    if (missingStatus === 'unavailable') {
      safeReport('ticket_email_bssdk_captcha_unavailable', {});
    }
    return {
      initialized: false,
      status: missingStatus
    };
  }

  const result: CaptchaInitResult = {
    initialized: true,
    status: 'ready'
  };
  let initCallCompleted = false;

  try {
    captcha.init(
      {
        commonOptions: {
          aid,
          iid: '0',
          did: '0'
        },
        captchaOptions: {
          showMode: 'mask',
          successCb: () => undefined
        }
      },
      () => {
        result.initialized = true;
        result.status = 'ready';
        if (initCallCompleted) {
          updateCaptchaState(true, 'ready');
        }
      },
      error => {
        result.initialized = false;
        result.status = 'failed';
        safeReport('ticket_email_bssdk_captcha_init_error', {
          error: String(error)
        });
        if (initCallCompleted) {
          updateCaptchaState(false, 'failed');
        }
      }
    );
    initCallCompleted = true;
    return result;
  } catch (error) {
    result.initialized = false;
    result.status = 'failed';
    safeReport('ticket_email_bssdk_captcha_init_error', {
      error: String(error)
    });
    return result;
  }
};

const recoverCaptcha = async (aid: number, recoveryTimeoutMs: number): Promise<void> => {
  const result = await initializeCaptcha(
    aid,
    recoveryTimeoutMs,
    'unavailable',
    CAPTCHA_RECOVERY_POLL_INTERVAL_MS
  );
  updateCaptchaState(result.initialized, result.status);
  if (result.status === 'ready') {
    safeReport('ticket_email_bssdk_captcha_recovered', {});
  }
};

const makeFailureState = (
  runtimeConfig: BssdkRuntimeConfig,
  options: BssdkInitOptions,
  error: unknown
): BssdkInitState => {
  const state = setState({
    status: 'failed',
    loaderUrl: runtimeConfig.loaderUrl,
    options,
    captchaInitialized: false,
    captchaStatus: 'failed',
    error: String(error)
  });
  safeReport('ticket_email_bssdk_init_error', {
    error: state.error ?? 'unknown',
    region: options.region,
    is_boe: options.isBoe
  });
  return state;
};

export const initializeBssdk = ({
  timeoutMs = DEFAULT_INIT_TIMEOUT_MS,
  captchaDiscoveryTimeoutMs = CAPTCHA_DISCOVERY_TIMEOUT_MS,
  captchaRecoveryTimeoutMs = CAPTCHA_RECOVERY_TIMEOUT_MS,
  aid = APP_ID
}: InitializeBssdkOptions = {}): Promise<BssdkInitState> => {
  if (typeof window === 'undefined' || typeof document === 'undefined') {
    return Promise.resolve({
      status: 'unavailable',
      captchaInitialized: false,
      captchaStatus: 'unavailable'
    });
  }

  if (window.__TICKET_EMAIL_BSSDK_INIT_PROMISE__) {
    return window.__TICKET_EMAIL_BSSDK_INIT_PROMISE__;
  }

  const runtimeConfig = getRuntimeConfig();
  if (!runtimeConfig) {
    const state: BssdkInitState = {
      status: 'unavailable',
      captchaInitialized: false,
      captchaStatus: 'unavailable',
      error: `Unsupported IDC: ${window.__SELLER_APP_ENVS__?.idc?.idc_name ?? 'unknown'}`
    };
    setState(state);
    safeReport('ticket_email_bssdk_unsupported_idc', {
      idc_name: window.__SELLER_APP_ENVS__?.idc?.idc_name ?? 'unknown',
      hostname: window.location.hostname
    });
    const unavailablePromise = Promise.resolve(state);
    window.__TICKET_EMAIL_BSSDK_INIT_PROMISE__ = unavailablePromise;
    return unavailablePromise;
  }

  const options = buildBssdkInitOptions(aid, runtimeConfig);
  setState({
    status: 'loading',
    loaderUrl: runtimeConfig.loaderUrl,
    options,
    captchaInitialized: false,
    captchaStatus: 'pending'
  });

  const initialization = (async (): Promise<BssdkInitState> => {
    try {
      await loadBssdkScript(runtimeConfig.loaderUrl);
      if (!window.lucifer?.init) {
        throw new Error('window.lucifer.init is unavailable');
      }

      await Promise.resolve(window.lucifer.init(options));
      const captcha = await initializeCaptcha(aid, captchaDiscoveryTimeoutMs, 'pending');
      const state = setState({
        status: 'ready',
        loaderUrl: runtimeConfig.loaderUrl,
        options,
        captchaInitialized: captcha.initialized,
        captchaStatus: captcha.status
      });
      safeReport('ticket_email_bssdk_init_success', {
        region: options.region,
        is_boe: options.isBoe,
        captcha_initialized: captcha.initialized,
        captcha_status: captcha.status
      });
      if (captcha.status === 'pending') {
        void recoverCaptcha(aid, captchaRecoveryTimeoutMs);
      }
      return state;
    } catch (error) {
      return makeFailureState(runtimeConfig, options, error);
    }
  })();

  let timeoutId: number | undefined;
  const timeout = new Promise<BssdkInitState>(resolve => {
    timeoutId = window.setTimeout(() => {
      resolve(makeFailureState(runtimeConfig, options, new Error(`BSSDK init timed out after ${timeoutMs}ms`)));
    }, timeoutMs);
  });

  const initPromise = Promise.race([initialization, timeout]).then(state => {
    if (timeoutId !== undefined) {
      window.clearTimeout(timeoutId);
    }
    return state;
  });

  window.__TICKET_EMAIL_BSSDK_INIT_PROMISE__ = initPromise;
  return initPromise;
};

export const waitForBssdkReady = (): Promise<BssdkInitState> => initializeBssdk();

export { BSSDK_INITIAL_PATH_LIST, BSSDK_LOADER_URLS, BssdkMode } from './config';
